// Line chart body. Extracted from the legacy FeatureChart, minus all
// chrome (card border, title, header buttons, fullscreen modal — those
// live in the container now).
//
// Fullscreen-only interactions kept here because they're chart-specific:
//   * Brush below the curve for range selection within the window.
//   * Cursor-anchored wheel zoom into the brush range. Attached natively
//     so we can preventDefault — React's synthetic onWheel is passive.
// The container handles document scroll-lock when its modal opens.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import type { ChartBodyProps } from '../types';
import { useTimeRangeState, windowForAnchor, withinWindow } from '../windowRange';
import { numericExtent, numericTickFormatter, numericYAxisWidth } from '../axisWidth';
import ChartTimeRangePicker from '../ChartTimeRangePicker';
import BrushSlider from '../BrushSlider';
import VerticalBrushSlider from '../VerticalBrushSlider';
import { dayTicks, formatDateLabel, yearBoundaries, yearBoundaryLineProps } from '../timeAxis';

interface TimeSeriesDataPoint {
  date: number; // timestamp
  value: number;
}

const LineChartBody: React.FC<ChartBodyProps> = ({
  featureId, unit, config, fullscreen, verticalBrushVisible = false,
}) => {
  const [rawData, setRawData] = useState<TimeSeriesDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [brushRange, setBrushRange] = useState<{ startIndex: number; endIndex: number } | null>(null);
  const [yRange, setYRange] = useState<{ startValue: number; endValue: number } | null>(null);

  const dataDates = useMemo(() => rawData.map((r) => r.date), [rawData]);
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );
  const data = useMemo(
    () => rawData.filter((d) => withinWindow(d.date, win)),
    [rawData, win],
  );

  const fullscreenChartRef = useRef<HTMLDivElement>(null);
  const chartDivRef = useRef<HTMLDivElement>(null);
  // Offsets from the wrapper's left/right to the chart's plot-area
  // edges. Measured from Recharts' X-axis line so the brush slider
  // below lines up with where the data actually starts and ends.
  const [brushPad, setBrushPad] = useState<{ left: number; right: number }>({ left: 6, right: 6 });
  // Top/bottom counterparts for the optional vertical brush. Measured
  // from Recharts' Y-axis line so the track aligns with the plot area
  // edges. Defaults are slack to keep the handles inside the SVG when
  // measurement hasn't run yet.
  const [vBrushPad, setVBrushPad] = useState<{ top: number; bottom: number }>({ top: 8, bottom: 8 });
  // Refs let the wheel listener read the latest data/brush without re-attaching on every change.
  const dataRef = useRef(data);
  const brushRangeRef = useRef(brushRange);
  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { brushRangeRef.current = brushRange; }, [brushRange]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`/api/features/timeseries/${featureId}`)
      .then((response) => {
        if (cancelled) return;
        const processed = response.data.map((item: any) => ({
          ...item,
          date: new Date(item.date).getTime(),
        }));
        setRawData(processed);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to fetch time series data:', err);
        if (!cancelled) setError('Failed to load chart data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  // Drop any brush selection when the visible window changes — a brush
  // over the old window's indices makes no sense in the new one. Same
  // logic for the Y brush: values may have shifted out of view.
  useEffect(() => {
    setBrushRange(null);
    setYRange(null);
  }, [data]);

  // Toggling the vertical brush off resets the Y zoom — otherwise the
  // chart would stay zoomed with no visible affordance to undo it.
  useEffect(() => {
    if (!verticalBrushVisible) setYRange(null);
  }, [verticalBrushVisible]);

  // The effect above clears brushRange asynchronously, so for the one
  // render between a `data` change and the effect committing, brushRange
  // still holds indices for the old, larger data array. Treat it as
  // cleared if its indices are out of bounds — otherwise BrushSlider's
  // formatLabel(idx) reaches into data[idx] past the new end and crashes
  // the page with "Cannot read properties of undefined".
  const validBrushRange = useMemo(() => {
    if (!brushRange) return null;
    if (data.length === 0) return null;
    if (brushRange.startIndex < 0 || brushRange.endIndex >= data.length) return null;
    if (brushRange.startIndex > brushRange.endIndex) return null;
    return brushRange;
  }, [brushRange, data.length]);

  // Re-run on data changes too: the chart div only mounts after data loads
  // (we render a spinner before then), so the ref isn't valid yet on the
  // first run when fullscreen=true. Also re-attaches if the window filter
  // swaps the underlying data array.
  //
  // Skipped when the vertical brush is visible: in that mode each brush
  // owns wheel events over its own wrap, and a chart-body handler would
  // surprise-zoom X whenever the user tried to wheel the Y brush near
  // (but not over) it.
  useEffect(() => {
    if (!fullscreen) return;
    if (data.length === 0) return;
    if (verticalBrushVisible) return;
    const el = fullscreenChartRef.current;
    if (!el) return;

    const handler = (e: WheelEvent) => {
      const d = dataRef.current;
      if (d.length < 2) return;
      e.preventDefault();

      const r = brushRangeRef.current;
      const startIdx = r?.startIndex ?? 0;
      const endIdx = r?.endIndex ?? d.length - 1;
      const width = endIdx - startIdx + 1;

      const factor = e.deltaY < 0 ? 0.85 : 1 / 0.85;
      const newWidth = Math.max(2, Math.min(d.length, Math.round(width * factor)));
      if (newWidth === width) return;

      const rect = el.getBoundingClientRect();
      const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

      const cursorIdx = startIdx + relX * (width - 1);
      let newStart = Math.round(cursorIdx - relX * (newWidth - 1));
      let newEnd = newStart + newWidth - 1;
      if (newStart < 0) { newStart = 0; newEnd = newWidth - 1; }
      if (newEnd > d.length - 1) { newEnd = d.length - 1; newStart = newEnd - newWidth + 1; }

      setBrushRange({ startIndex: newStart, endIndex: newEnd });
    };

    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [fullscreen, data.length, verticalBrushVisible]);

  // Touch counterpart: pinch-to-zoom around the two-finger midpoint.
  // Only activates when both fingers land inside the chart area, so a
  // single-finger drag on a brush handle below still works. Same opt-out
  // as the wheel handler when the vertical brush is visible — the brush
  // sliders own their own gestures in that mode.
  useEffect(() => {
    if (!fullscreen) return;
    if (data.length === 0) return;
    if (verticalBrushVisible) return;
    const el = fullscreenChartRef.current;
    if (!el) return;

    let pinch: { initStart: number; initEnd: number; initDist: number; anchorRelX: number } | null = null;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) { pinch = null; return; }
      const chartEl = chartDivRef.current;
      if (!chartEl) return;
      const cr = chartEl.getBoundingClientRect();
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const inChart = (t: Touch) =>
        t.clientX >= cr.left && t.clientX <= cr.right &&
        t.clientY >= cr.top && t.clientY <= cr.bottom;
      if (!inChart(t1) || !inChart(t2)) return;
      e.preventDefault();
      const d = dataRef.current;
      if (d.length < 2) return;
      const r = brushRangeRef.current;
      const rect = el.getBoundingClientRect();
      pinch = {
        initStart: r?.startIndex ?? 0,
        initEnd: r?.endIndex ?? d.length - 1,
        initDist: Math.max(1, Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)),
        anchorRelX: Math.max(0, Math.min(1, ((t1.clientX + t2.clientX) / 2 - rect.left) / rect.width)),
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pinch || e.touches.length !== 2) return;
      e.preventDefault();
      const d = dataRef.current;
      if (d.length < 2) return;
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const scale = dist / pinch.initDist;
      const initWidth = pinch.initEnd - pinch.initStart + 1;
      const newWidth = Math.max(2, Math.min(d.length, Math.round(initWidth / scale)));
      const cursorIdx = pinch.initStart + pinch.anchorRelX * (initWidth - 1);
      let newStart = Math.round(cursorIdx - pinch.anchorRelX * (newWidth - 1));
      let newEnd = newStart + newWidth - 1;
      if (newStart < 0) { newStart = 0; newEnd = newWidth - 1; }
      if (newEnd > d.length - 1) { newEnd = d.length - 1; newStart = newEnd - newWidth + 1; }
      const cur = brushRangeRef.current;
      if (cur && cur.startIndex === newStart && cur.endIndex === newEnd) return;
      setBrushRange({ startIndex: newStart, endIndex: newEnd });
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) pinch = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [fullscreen, data.length, verticalBrushVisible]);

  // Recharts no longer slices the data for us — we drove that via its
  // Brush before. Slice ourselves so the chart only renders the
  // brushed window.
  const shownData = useMemo(() => {
    if (!validBrushRange) return data;
    return data.slice(validBrushRange.startIndex, validBrushRange.endIndex + 1);
  }, [data, validBrushRange]);

  // Measure Recharts' X- and Y-axis lines to learn the plot-area's
  // extents, then expose them as padding to the brush sliders so the
  // tracks line up with the chart's data edges. Re-runs when anything
  // that could shift the plot area changes; functional setters guard
  // against redundant updates.
  useLayoutEffect(() => {
    if (!fullscreen) return;
    const div = chartDivRef.current;
    if (!div) return;
    const measure = () => {
      const dr = div.getBoundingClientRect();
      const xLine = div.querySelector(
        '.recharts-xAxis .recharts-cartesian-axis-line',
      ) as SVGLineElement | null;
      if (xLine) {
        const lr = xLine.getBoundingClientRect();
        if (lr.width > 0) {
          const left = Math.max(0, Math.round(lr.left - dr.left));
          const right = Math.max(0, Math.round(dr.right - lr.right));
          setBrushPad((prev) => (prev.left === left && prev.right === right) ? prev : { left, right });
        }
      }
      const yLine = div.querySelector(
        '.recharts-yAxis .recharts-cartesian-axis-line',
      ) as SVGLineElement | null;
      if (yLine) {
        const lr = yLine.getBoundingClientRect();
        if (lr.height > 0) {
          const top = Math.max(0, Math.round(lr.top - dr.top));
          const bottom = Math.max(0, Math.round(dr.bottom - lr.bottom));
          setVBrushPad((prev) => (prev.top === top && prev.bottom === bottom) ? prev : { top, bottom });
        }
      }
    };
    measure();
    const rafId = requestAnimationFrame(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(div);
    // ResizeObserver / window events on iOS rotation fire before the
    // layout has settled — re-measure on rAF and after ~300ms to catch
    // the post-reflow geometry.
    const deferred = () => {
      measure();
      requestAnimationFrame(measure);
      setTimeout(measure, 300);
    };
    window.addEventListener('resize', deferred);
    window.addEventListener('orientationchange', deferred);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', deferred);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener('resize', deferred);
      window.removeEventListener('orientationchange', deferred);
      vv?.removeEventListener('resize', deferred);
    };
  }, [fullscreen, shownData]);

  // Drive ~5 ticks ourselves; recharts' time-scale auto-ticks ignore
  // tickCount as a hard cap and pile up when the brush is dragged. Each sits on
  // a day, so a few-day span doesn't repeat or misplace one (`dayTicks`).
  const xTicks = useMemo<number[] | undefined>(
    () =>
      shownData.length === 0
        ? undefined
        : dayTicks(shownData[0].date, shownData[shownData.length - 1].date),
    [shownData],
  );

  const tickLabels = useMemo<Map<number, string>>(() => {
    const map = new Map<number, string>();
    if (!xTicks) return map;
    for (const ts of xTicks) {
      map.set(ts, formatDateLabel(ts, false));
    }
    return map;
  }, [xTicks]);

  // Year boundaries inside the visible domain — rendered as thin gray
  // vertical lines so the user can see where the calendar year flips
  // without burning a tick label on it. The label on each line shows
  // the year *before* the boundary; the higher year is implicit (and
  // also visible on the brush slider below the chart).
  const yearLines = useMemo<number[]>(() => {
    if (shownData.length < 2) return [];
    const dates = shownData.map((p) => p.date);
    return yearBoundaries(Math.min(...dates), Math.max(...dates));
  }, [shownData]);

  // Bounds for the vertical brush. Rating-shaped features use their
  // configured fixed scale; everything else uses the visible data
  // extent. A flat series (min === max) gets a small slack span so
  // the two handles can still separate.
  const isRating = config?.type === 'rating' || config?.type === 'labeled_rating';
  const yBounds = useMemo<{ min: number; max: number }>(() => {
    if (isRating) {
      return { min: 1, max: config?.params?.max || 5 };
    }
    if (shownData.length === 0) return { min: 0, max: 1 };
    let mn = shownData[0].value;
    let mx = shownData[0].value;
    for (const p of shownData) {
      if (p.value < mn) mn = p.value;
      if (p.value > mx) mx = p.value;
    }
    if (mn === mx) {
      const slack = mn === 0 ? 1 : Math.abs(mn) * 0.1;
      return { min: mn - slack, max: mn + slack };
    }
    return { min: mn, max: mx };
  }, [shownData, isRating, config?.params?.max]);

  // Same year-switching rule as the X-axis ticks, but applied to every
  // visible data point so tooltips can look up a date label.
  const tooltipDateLabels = useMemo<Map<number, string>>(() => {
    const map = new Map<number, string>();
    let lastYear: number | undefined;
    for (const p of shownData) {
      const yr = new Date(p.date).getFullYear();
      const withYear = lastYear === undefined || lastYear !== yr;
      map.set(p.date, formatDateLabel(p.date, withYear));
      lastYear = yr;
    }
    return map;
  }, [shownData]);

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (rawData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No numeric data available for this feature.</p>
        <p className="text-sm">Add check-ins with numeric values to see a chart.</p>
      </div>
    );
  }

  const formatAxisTick = (timestamp: number) =>
    tickLabels.get(timestamp) ?? formatDateLabel(timestamp, true);

  // Give the YAxis enough width to fit its tick labels on one line, so we
  // don't need to pad the AreaChart's left margin to compensate (which
  // wastes a chunk of horizontal space, particularly on mobile).
  const yAxisWidth = (() => {
    if (config?.type === 'labeled_rating' && config.params?.labels) {
      const maxLabelLength = Math.max(...config.params.labels.map((label) => label.length));
      return Math.min(90, Math.max(40, maxLabelLength * 8 + 10));
    }
    // Numeric axis: size to the widest tick label so long values (e.g. "22.24")
    // aren't clipped, and short ones don't leave a fat margin.
    const [lo, hi] = yRange
      ? [yRange.startValue, yRange.endValue]
      : isRating
        ? [1, config?.params?.max || 5]
        : numericExtent(shownData.map((d) => d.value));
    return numericYAxisWidth(lo, hi, { fontSizePx: 15 });
  })();

  const emptyWindow = data.length === 0;

  return (
    <div
      ref={fullscreen ? fullscreenChartRef : undefined}
      className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'}
      style={{ touchAction: 'none' }}
    >
      <ChartTimeRangePicker state={range} />
      {emptyWindow ? (
        <div className={`flex items-center justify-center text-gray-500 ${fullscreen ? 'flex-1 min-h-0' : 'h-80'}`}>
          <p>No values in this period.</p>
        </div>
      ) : (
        <>
          <div className={fullscreen ? 'flex-1 min-h-0 flex' : 'h-80 flex'}>
            <div ref={chartDivRef} className="flex-1 min-w-0 h-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={shownData}
                margin={{
                  top: 5,
                  right: fullscreen ? 30 : 10,
                  left: 0,
                  bottom: 25,
                }}
              >
                <XAxis
                  dataKey="date"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={formatAxisTick}
                  ticks={xTicks}
                  angle={-45}
                  textAnchor="end"
                  height={fullscreen ? 70 : 50}
                  scale="time"
                  padding={{ left: 0, right: 0 }}
                  interval={0}
                  tick={{ fontSize: 15 }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 15 }}
                  tickLine={false}
                  width={yAxisWidth}
                  domain={
                    yRange
                      ? [yRange.startValue, yRange.endValue]
                      : (isRating
                          ? [1, config?.params?.max || 5]
                          : ['dataMin', 'dataMax'])
                  }
                  // When the user is zoomed in on a Y sub-range, tell Recharts
                  // to respect that domain instead of widening it back to fit
                  // all the data — defeats the purpose of zooming otherwise.
                  allowDataOverflow={!!yRange}
                  // Rating values are integers; without this recharts auto-ticks at
                  // 1.5, 2.5, ... which the formatter then prints as bare numbers
                  // alongside the labels (a labeled_rating with max=3 has no label
                  // for "2.5", so the formatter falls back to value.toString()).
                  allowDecimals={!isRating || !!yRange}
                  tickFormatter={
                    isRating && !yRange
                      ? (value) => {
                          if (config?.type === 'labeled_rating' && config.params?.labels && config.params.labels[value - 1]) {
                            return config.params.labels[value - 1];
                          }
                          return value.toString();
                        }
                      : (yRange
                          // With a brush-chosen domain, Recharts includes the
                          // exact endValue as a tick. Default toString on a
                          // fractional float (e.g. 5.8957654723127035) then
                          // overflows the label cell. Trim to ~3 significant
                          // digits so the tick reads as 5.9.
                          ? (value) => {
                              if (value === 0) return '0';
                              return parseFloat(value.toPrecision(3)).toString();
                            }
                          // Default numeric axis: trim raw floats so the label
                          // matches the width we measured for the axis.
                          : numericTickFormatter)
                  }
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const value = payload[0].value as number;
                    const ts = payload[0].payload.date as number;
                    let displayValue = value.toString();
                    if (config?.type === 'labeled_rating' && config.params?.labels && config.params.labels[value - 1]) {
                      displayValue = config.params.labels[value - 1];
                    } else if (unit) {
                      displayValue = `${value} ${unit}`;
                    }
                    const dateLabel = tooltipDateLabels.get(ts) ?? formatDateLabel(ts, true);
                    return (
                      <div className="bg-white border border-gray-200 rounded shadow-md px-2 py-1 text-sm">
                        <div className="text-gray-600">{dateLabel}</div>
                        <div className="text-gray-900">{displayValue}</div>
                      </div>
                    );
                  }}
                />
                {yearLines.map((ts) => <ReferenceLine key={ts} {...yearBoundaryLineProps(ts)} />)}
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#8884d8"
                  strokeWidth={2}
                  fill="#8884d8"
                  fillOpacity={0.2}
                  dot={false}
                  activeDot={{ r: 8 }}
                  name="Value"
                  isAnimationActive={true}
                />
              </AreaChart>
            </ResponsiveContainer>
            </div>
            {verticalBrushVisible && shownData.length > 1 && yBounds.max > yBounds.min && (
              <VerticalBrushSlider
                min={yBounds.min}
                max={yBounds.max}
                startValue={yRange?.startValue ?? yBounds.min}
                endValue={yRange?.endValue ?? yBounds.max}
                onChange={(r) => setYRange(r)}
                padTop={vBrushPad.top}
                padBottom={vBrushPad.bottom}
              />
            )}
          </div>
          {fullscreen && data.length > 1 && (
            <BrushSlider
              length={data.length}
              startIndex={validBrushRange?.startIndex ?? 0}
              endIndex={validBrushRange?.endIndex ?? data.length - 1}
              onChange={(r) => setBrushRange(r)}
              padLeft={brushPad.left}
              padRight={brushPad.right}
              formatLabel={(idx) => {
                const d = new Date(data[idx].date);
                return `${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()}`;
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default LineChartBody;
